import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  FileText,
  Target,
  Layers,
  Construction,
  Droplets,
  HelpCircle,
  Ruler,
  Settings,
  Shield,
  Building2,
  Anchor,
  Wrench
} from 'lucide-react';

const SlurryWallGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Qu'est-ce qu'une paroi moulée ?" },
    { id: 'section-2', title: "Les étapes de réalisation" },
    { id: 'section-3', title: "Le matériel : benne et hydrofraise" },
    { id: 'section-4', title: "Paramètres clés de conception" },
    { id: 'section-5', title: "Les avantages de la technique" },
    { id: 'section-6', title: "Applications" },
    { id: 'section-7', title: "Questions fréquentes" },
  ];

  const faqs = [
    {
      question: "Benne ou hydrofraise : comment choisir ?",
      answer: "Le choix dépend principalement de la nature du terrain. La benne convient à une large gamme de sols et reste économique. L'hydrofraise s'impose dans les terrains très résistants et pour les grandes profondeurs, avec un excellent contrôle de la verticalité."
    },
    {
      question: "Quelle profondeur peut atteindre une paroi moulée ?",
      answer: "Les profondeurs de 35 à 50 m sont courantes. À l'hydrofraise, on peut dépasser 90 à 100 m, voire davantage sur des ouvrages exceptionnels."
    },
    {
      question: "À quoi sert la boue de bentonite ?",
      answer: "Elle maintient les parois de la tranchée pendant l'excavation : elle forme un « cake » étanche et exerce une pression hydrostatique qui s'oppose à l'éboulement. Elle est recyclée pendant le bétonnage."
    },
    {
      question: "Pourquoi préférer une paroi moulée à des palplanches ?",
      answer: "Pour sa bien plus faible déformabilité et sa capacité à assurer étanchéité et fonction définitive, essentielles en site urbain à proximité d'avoisinants."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Paroi moulée : technique, étapes de réalisation et applications | Guide complet",
    "description": "Tout sur la paroi moulée : étapes de réalisation, benne et hydrofraise, boue bentonite, paramètres de conception et applications en soutènement profond.",
    "image": "https://geots.consulting/og-images/guide-paroi-moulee.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-04",
    "dateModified": "2026-07-04"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Steps data
  const steps = [
    {
      number: 1,
      title: "La murette-guide",
      description: "On réalise d'abord au sol deux petits murets en béton parallèles, espacés de l'épaisseur de la future paroi. Cette murette-guide matérialise le tracé, guide l'outil d'excavation (benne ou hydrofraise), sert de réserve de boue et permet de caler les cages d'armatures.",
      icon: Ruler
    },
    {
      number: 2,
      title: "L'excavation par panneaux",
      description: "Le creusement se fait par panneaux de longueur limitée (de l'ordre de 5 à 7 m), pour garantir la stabilité pendant les travaux. En présence de mitoyens, on respecte des règles de distance strictes. Selon le terrain, on excave avec une benne (mécanique ou hydraulique) ou une hydrofraise.",
      icon: Construction
    },
    {
      number: 3,
      title: "La stabilisation à la boue",
      description: "Au fur et à mesure du creusement, le terrain retiré est remplacé par une boue de forage (généralement à base de bentonite, parfois des polymères selon la géologie). Cette boue forme sur les parois un dépôt étanche appelé « cake » et exerce une pression hydrostatique qui empêche l'éboulement de la tranchée.",
      icon: Droplets
    },
    {
      number: 4,
      title: "La mise en place de l'armature",
      description: "Une fois le panneau excavé à la profondeur voulue, la boue est recyclée (dessablage) pour limiter les particules en suspension, puis on descend la cage d'armatures dans la tranchée pleine de boue. Cette cage peut être pré-équipée (réservations pour tirants d'ancrage, par exemple).",
      icon: Anchor
    },
    {
      number: 5,
      title: "Le bétonnage au tube plongeur",
      description: "Le bétonnage se fait par la technique du tube plongeur : le béton est injecté depuis le fond du panneau et remonte progressivement, chassant la boue vers le haut (le béton, plus dense à ~2,5 t/m³, se substitue à la boue à ~1,05 t/m³). La boue chassée est aspirée et recyclée.",
      icon: Layers
    },
    {
      number: 6,
      title: "La jonction entre panneaux",
      description: "L'étanchéité entre panneaux adjacents est assurée soit par un joint métallique provisoire (type CWS) laissant un joint water-stop dans le béton, soit par recoupement dans le béton du panneau voisin (possible à l'hydrofraise).",
      icon: Settings
    },
    {
      number: 7,
      title: "La poutre de couronnement et le terrassement",
      description: "Une poutre de couronnement peut solidariser la tête des panneaux. Une fois la paroi achevée, le terrassement à l'intérieur peut commencer, avec mise en place progressive de butons ou de tirants d'ancrage pour maîtriser les déformations jusqu'au fond de fouille.",
      icon: Building2
    }
  ];

  return (
    <>
      <Helmet>
        <title>Paroi moulée : technique, étapes de réalisation et applications | GEOTS</title>
        <meta name="description" content="Tout sur la paroi moulée : étapes de réalisation, benne et hydrofraise, boue bentonite, paramètres de conception et applications en soutènement profond." />
        <link rel="canonical" href="https://geots.consulting/guides/paroi-moulee-technique-realisation" />
        <meta property="og:title" content="Paroi moulée : technique, étapes de réalisation et applications" />
        <meta property="og:description" content="Tout sur la paroi moulée : étapes de réalisation, benne et hydrofraise, boue bentonite, paramètres de conception et applications en soutènement profond." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://geots.consulting/guides/paroi-moulee-technique-realisation" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-[#635BFF]">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Paroi moulée</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  GUIDE TECHNIQUE
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mis à jour le 4 juillet 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  14 min de lecture
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                La paroi moulée :
                <span className="text-[#00D4FF]"> guide complet de la technique, des étapes et des applications</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                La paroi moulée est la référence des soutènements profonds en milieu urbain : parkings enterrés, 
                stations de métro, quais, tunnels, barrages. Comment se réalise-t-elle ? Quels sont ses paramètres clés, 
                ses avantages et ses domaines d'emploi ? Ce guide fait le tour complet de la technique.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/travaux-fondations" 
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Demander un devis travaux
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/annuaire/travaux-fondations-speciales" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Trouver une entreprise
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#635BFF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>

                {/* Links to related guides */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-xs text-gray-500 mb-2">Guides connexes</p>
                  <Link 
                    to="/guides/comment-choisir-fondations-speciales"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1 mb-2"
                  >
                    <Layers className="w-4 h-4" />
                    Fondations spéciales
                  </Link>
                  <Link 
                    to="/guides/comment-choisir-foreuse-geotechnique"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1 mb-2"
                  >
                    <Wrench className="w-4 h-4" />
                    Choisir une foreuse
                  </Link>
                  <Link 
                    to="/guides/comment-choisir-etude-geotechnique"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1"
                  >
                    <FileText className="w-4 h-4" />
                    Choisir son étude de sol
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              <article className="prose prose-lg max-w-none">
                
                {/* Section 1 */}
                <section id="section-1" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                    Qu'est-ce qu'une paroi moulée ?
                  </h2>
                  
                  <p className="text-gray-700 mb-6">
                    Une paroi moulée est un <strong>mur en béton armé coulé directement dans le sol</strong>. 
                    Le principe est simple dans son idée, exigeant dans sa mise en œuvre : on creuse une tranchée 
                    que l'on maintient constamment remplie de boue durant l'excavation, puis on y coule le béton.
                  </p>

                  <p className="text-gray-700 mb-6">
                    La paroi est réalisée par <strong>panneaux successifs</strong> de forme rectangulaire, contigus, 
                    jusqu'à former un mur continu autour du périmètre à soutenir. Grâce à sa forte inertie, 
                    la paroi moulée est <strong>beaucoup moins déformable</strong> qu'une paroi berlinoise ou 
                    qu'un rideau de palplanches — un atout décisif pour les excavations en site urbain dense, 
                    à proximité de mitoyens.
                  </p>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6 mb-8">
                    <Lightbulb className="w-8 h-8 text-[#00D4FF] mb-3" />
                    <p className="text-[#0A2540]">
                      <strong>Multi-fonctionnelle :</strong> Au-delà du soutènement, une paroi moulée peut 
                      assurer plusieurs fonctions simultanées : <strong>reprise de charges</strong> (fondation), 
                      <strong>étanchéité</strong> (écran d'eau), et <strong>mur définitif</strong> de l'ouvrage.
                    </p>
                  </div>
                </section>

                {/* Section 2 - Steps */}
                <section id="section-2" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                    Les étapes de réalisation d'une paroi moulée
                  </h2>

                  <div className="space-y-6">
                    {steps.map((step) => (
                      <div key={step.number} className="bg-white rounded-xl border shadow-sm p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold text-lg">
                              {step.number}
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                              <step.icon className="w-5 h-5 text-[#635BFF]" />
                              {step.title}
                            </h3>
                            <p className="text-gray-700">{step.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 3 - Equipment */}
                <section id="section-3" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                    Le matériel : benne à grue et hydrofraise
                  </h2>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white rounded-xl border-l-4 border-l-blue-500 shadow-sm p-6">
                      <h3 className="text-xl font-bold text-[#0A2540] mb-3">La benne (clamshell)</h3>
                      <p className="text-gray-700 mb-4">
                        Montée sur grue (à câble ou sur mât Kelly), elle offre une excavation verticale précise 
                        et contrôlée. Adaptée à une large gamme de sols (des mous aux durs), avec de faibles 
                        vibrations et nuisances, elle permet d'atteindre des profondeurs importantes.
                      </p>
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-sm text-blue-800"><strong>Idéale pour :</strong> Sols courants, budget maîtrisé</p>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border-l-4 border-l-purple-500 shadow-sm p-6">
                      <h3 className="text-xl font-bold text-[#0A2540] mb-3">L'hydrofraise</h3>
                      <p className="text-gray-700 mb-4">
                        Constituée de deux tambours à axe horizontal munis de pics tournant en sens opposés, 
                        elle broie le terrain et l'évacue en circulation inverse. Elle excave des terrains 
                        très résistants (jusqu'à ~80 MPa) sans trépannage et atteint de très grandes profondeurs 
                        (au-delà de 90-100 m).
                      </p>
                      <div className="bg-purple-50 rounded-lg p-3">
                        <p className="text-sm text-purple-800"><strong>Idéale pour :</strong> Roches dures, grandes profondeurs</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                    <h4 className="font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                      <Target className="w-5 h-5 text-yellow-600" />
                      Contrôle de la verticalité
                    </h4>
                    <p className="text-gray-700">
                      Sans précaution, la déviation de l'outil est de l'ordre de <strong>1 % de la hauteur excavée</strong>. 
                      Avec l'électronique embarquée mesurant la trajectoire en 3D et des corrections en temps réel, 
                      on peut la ramener à <strong>~0,3 %</strong>.
                    </p>
                  </div>
                </section>

                {/* Section 4 - Parameters */}
                <section id="section-4" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                    Paramètres clés de conception
                  </h2>

                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Paramètre</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Valeurs courantes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {[
                          ["Épaisseur de la paroi", "0,50 à 1,50 m (fréquemment 0,60 / 0,80 m)"],
                          ["Profondeur d'excavation", "35 à 50 m couramment, jusqu'à ~150 m à l'hydrofraise"],
                          ["Longueur des panneaux", "~5 à 7 m"],
                          ["Boue stabilisante", "Bentonite (ou polymères)"],
                          ["Résistance du béton", "C25/30 à C50/60"],
                          ["Acier d'armature", "B500B ou B500C"],
                          ["Tolérance de verticalité", "≤ 1/500 (jusqu'à ~0,3 %)"],
                          ["Joints", "Water-stop / recoupement (CWS, Tongue & Groove)"],
                        ].map(([param, value], i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-700 font-medium">{param}</td>
                            <td className="px-4 py-3 text-gray-600">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Section 5 - Advantages */}
                <section id="section-5" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">5</span>
                    Les avantages de la technique
                  </h2>

                  <div className="space-y-4 mb-8">
                    {[
                      { title: "Étanchéité et rigidité élevées", desc: "La forte inertie limite les déformations du sol et les venues d'eau." },
                      { title: "Maîtrise des déformations", desc: "En milieu urbain dense, à proximité de mitoyens sensibles." },
                      { title: "Grandes profondeurs accessibles", desc: "En particulier à l'hydrofraise (jusqu'à 150 m)." },
                      { title: "Polyvalence", desc: "Une même paroi peut servir de soutènement provisoire, de mur définitif, de fondation et d'écran étanche." },
                      { title: "Solution durable et pérenne", desc: "Adaptée aux ouvrages complexes avec une durée de vie importante." },
                    ].map((advantage, i) => (
                      <div key={i} className="bg-white rounded-lg border p-4 flex items-start gap-3">
                        <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-[#0A2540]">{advantage.title}</h4>
                          <p className="text-sm text-gray-600">{advantage.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 6 - Applications */}
                <section id="section-6" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">6</span>
                    Applications
                  </h2>

                  <div className="grid md:grid-cols-2 gap-4 mb-8">
                    {[
                      { 
                        title: "Ouvrages urbains enterrés", 
                        examples: "Parkings souterrains, sous-sols de bâtiments, trémies routières, stations et tunnels de métro.",
                        icon: Building2,
                        color: "blue"
                      },
                      { 
                        title: "Infrastructures ferroviaires", 
                        examples: "Sécurisation des lignes, soutènement des fouilles à proximité des voies, protection contre les chutes de blocs.",
                        icon: Construction,
                        color: "orange"
                      },
                      { 
                        title: "Ouvrages hydrauliques", 
                        examples: "Quais, murs de soutènement portuaires, bassins de stockage, et renforcement/étanchéité de barrages.",
                        icon: Droplets,
                        color: "cyan"
                      },
                      { 
                        title: "Stabilité des pentes de déblai", 
                        examples: "En pied de talus pour limiter les glissements.",
                        icon: Shield,
                        color: "green"
                      },
                    ].map((app, i) => (
                      <div key={i} className={`bg-white rounded-xl border p-5 hover:shadow-md transition-shadow`}>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                          app.color === 'blue' ? 'bg-blue-100' :
                          app.color === 'orange' ? 'bg-orange-100' :
                          app.color === 'cyan' ? 'bg-cyan-100' :
                          'bg-green-100'
                        }`}>
                          <app.icon className={`w-5 h-5 ${
                            app.color === 'blue' ? 'text-blue-600' :
                            app.color === 'orange' ? 'text-orange-600' :
                            app.color === 'cyan' ? 'text-cyan-600' :
                            'text-green-600'
                          }`} />
                        </div>
                        <h4 className="font-bold text-[#0A2540] mb-2">{app.title}</h4>
                        <p className="text-sm text-gray-600">{app.examples}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 7 - FAQ */}
                <section id="section-7" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">7</span>
                    Questions fréquentes
                  </h2>

                  <div className="space-y-3">
                    {faqs.map((faq, i) => (
                      <div key={i} className="bg-white rounded-xl border overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-[#0A2540]">{faq.question}</span>
                          {openFaq === i ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {openFaq === i && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* CTA Section */}
                <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white mb-16">
                  <h2 className="text-2xl font-bold mb-4">Le rôle du matériel et du suivi</h2>
                  <p className="mb-6 text-white/90">
                    La qualité d'une paroi moulée repose sur la précision de l'excavation (verticalité), 
                    le contrôle permanent de la boue et le suivi du bétonnage. Instrumentation de mesure, 
                    capteurs de déviation, contrôle des propriétés de la boue et auscultation du béton 
                    sont autant de maillons essentiels.
                  </p>
                  <p className="mb-6 text-white/90">
                    GEOTS met en relation les acteurs de la géotechnique et des fondations spéciales : 
                    entreprises de travaux, bureaux d'études, loueurs de matériel de forage et d'instrumentation, 
                    prestataires spécialisés.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/travaux-fondations" className="bg-white text-[#635BFF] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2">
                      Demander un devis
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <Link to="/annuaire/travaux-fondations-speciales" className="bg-white/20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors">
                      Trouver une entreprise
                    </Link>
                  </div>
                </section>

                {/* Related Guides */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Pour aller plus loin</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Link 
                      to="/guides/comment-choisir-fondations-speciales"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir ses fondations spéciales ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/comment-choisir-foreuse-geotechnique"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir une foreuse géotechnique ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  </div>
                </section>

                {/* Footer note */}
                <div className="text-center text-gray-500 text-sm border-t pt-8">
                  <p>
                    Ce guide est fourni à titre informatif. La conception et la réalisation d'une paroi moulée 
                    doivent s'appuyer sur une étude géotechnique et l'intervention d'un bureau d'études 
                    et d'une entreprise spécialisés.
                  </p>
                  <p className="mt-2">Dernière mise à jour : 4 juillet 2026</p>
                </div>
              </article>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default SlurryWallGuide;
